// pages/Distribution.jsx
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  alpha,
  Stack,
  Divider,
} from '@mui/material';
import {
  LocalShipping,
  Security,
  GpsFixed,
  Verified,
  Speed,
  Storage,
  Flight,
  DirectionsBoat,
  Diamond,
  Public,
  Shield,
  WorkspacePremium,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Premium animations
const goldShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const floatSlow = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(20px, -15px) rotate(2deg);
  }
  66% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatMedium = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(-15px, -10px) rotate(-2deg);
  }
  66% {
    transform: translate(15px, 8px) rotate(1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatFast = keyframes`
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  50% {
    transform: translate(8px, -8px) scale(1.05);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
`;

const shimmerBackground = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0.3;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

// Abstract Background Component
const AbstractBackground = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 30%, #0a0a2a 0%, #0d0d35 50%, #060620 100%)',
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '-5%',
        width: '40%',
        height: '40%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatSlow} 25s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatMedium} 30s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, rgba(0,100,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatFast} 20s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(0,150,255,0.06) 0%, rgba(0,150,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatSlow} 22s ease-in-out infinite reverse`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: '200px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
        transform: 'rotate(25deg)',
        animation: `${shimmerBackground} 4s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '25%',
        right: '15%',
        width: '300px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
        transform: 'rotate(-15deg)',
        animation: `${shimmerBackground} 5s ease-in-out infinite 1s`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '60%',
        left: '10%',
        width: '150px',
        height: '150px',
        border: '1px solid rgba(212,175,55,0.08)',
        borderRadius: '50%',
        animation: `${floatMedium} 18s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        right: '25%',
        width: '100px',
        height: '100px',
        border: '1px solid rgba(0,150,255,0.1)',
        borderRadius: '50%',
        animation: `${floatFast} 15s ease-in-out infinite reverse`,
      }}
    />
    
    {[...Array(20)].map((_, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${2 + Math.random() * 4}px`,
          height: `${2 + Math.random() * 4}px`,
          background: `rgba(212, 175, 55, ${0.1 + Math.random() * 0.3})`,
          borderRadius: '50%',
          animation: `${floatFast} ${15 + Math.random() * 20}s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
    ))}
    
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }}
    />
  </Box>
);

// Reusable consistent card components
const StatCard = ({ icon, value, label, description, delay = 0 }) => (
  <Box
    sx={{
      animation: `${fadeInUp} 0.5s ease-out ${delay}s forwards`,
      opacity: 0,
      height: '100%',
    }}
  >
    <Card
      elevation={0}
      sx={{
        textAlign: 'center',
        py: 2.5,
        px: 1.5,
        height: 180,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: '#FFFFFF',
        border: `1px solid ${alpha('#D4AF37', 0.15)}`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha('#D4AF37', 0.4),
          boxShadow: `0 10px 25px ${alpha('#D4AF37', 0.15)}`,
        },
      }}
    >
      <Box sx={{ color: '#D4AF37', fontSize: 36, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0a0a2a', fontSize: { xs: 28, md: 32 } }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0a0a2a', mt: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>
        {description}
      </Typography>
    </Card>
  </Box>
);

const ServiceCard = ({ icon, title, description, delay = 0 }) => (
  <Box
    sx={{
      animation: `${fadeInUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s forwards`,
      opacity: 0,
      height: '100%',
    }}
  >
    <Card
      elevation={0}
      sx={{
        textAlign: 'center',
        height: 200,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: '#F8F9FC',
        border: `1px solid ${alpha('#D4AF37', 0.12)}`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha('#D4AF37', 0.35),
          boxShadow: `0 15px 30px ${alpha('#D4AF37', 0.12)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ fontSize: 40, color: '#D4AF37', mb: 1.5 }}>{icon}</Box>
        <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 700, mb: 0.5, fontSize: '1rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), fontSize: '0.75rem' }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

const TransportCard = ({ icon, name, time, capacity, routes, delay = 0 }) => (
  <Box
    sx={{
      animation: `${scaleIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s forwards`,
      opacity: 0,
      height: '100%',
    }}
  >
    <Card
      elevation={0}
      sx={{
        textAlign: 'center',
        height: 280,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#F8F9FC',
        border: `1px solid ${alpha('#D4AF37', 0.12)}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha('#D4AF37', 0.35),
        },
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Box sx={{ fontSize: 40, color: '#D4AF37', mb: 1.5 }}>{icon}</Box>
        <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 1.5, fontSize: '1rem' }}>
          {name}
        </Typography>
        <Divider sx={{ mb: 1.5, borderColor: alpha('#D4AF37', 0.15) }} />
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.6) }}>Delivery</Typography>
            <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 700 }}>{time}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.6) }}>Capacity</Typography>
            <Typography variant="caption" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{capacity}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.6) }}>Routes</Typography>
            <Typography variant="caption" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{routes}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  </Box>
);

const HubCard = ({ location, type, description, established, capacity, capabilities, delay = 0 }) => (
  <Box
    sx={{
      animation: `${fadeInLeft} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s forwards`,
      opacity: 0,
      height: '100%',
    }}
  >
    <Card
      elevation={0}
      sx={{
        height: '100%',
        minHeight: 320,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        border: `1px solid ${alpha('#D4AF37', 0.12)}`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha('#D4AF37', 0.35),
          boxShadow: `0 12px 25px ${alpha('#D4AF37', 0.12)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ color: '#0a0a2a', fontWeight: 800, fontSize: '1rem' }}>
            {location}
          </Typography>
          <Chip
            label={type}
            size="small"
            sx={{
              bgcolor: alpha('#D4AF37', 0.12),
              color: '#D4AF37',
              fontWeight: 600,
              fontSize: '0.6rem',
              height: 22,
            }}
          />
        </Box>
        
        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mb: 1.5, fontSize: '0.7rem', lineHeight: 1.4 }}>
          {description}
        </Typography>
        
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block', fontSize: '0.6rem' }}>
              Established
            </Typography>
            <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700, fontSize: '0.75rem' }}>
              {established}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), display: 'block', fontSize: '0.6rem' }}>
              Capacity
            </Typography>
            <Typography variant="body2" sx={{ color: '#D4AF37', fontWeight: 700, fontSize: '0.75rem' }}>
              {capacity}
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 1.5, borderColor: alpha('#D4AF37', 0.15) }} />
        
        <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 1, fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>
          Capabilities
        </Typography>
        <Grid container spacing={0.5}>
          {capabilities.map((cap) => (
            <Grid item xs={12} sm={6} key={cap}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Verified sx={{ color: '#D4AF37', fontSize: 10 }} />
                <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.7), fontSize: '0.65rem' }}>{cap}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  </Box>
);

const Distribution = () => {
  const hubs = [
    { 
      location: 'London, UK', 
      type: 'European Hub', 
      capabilities: ['Storage', 'Clearing', 'Air Freight', 'LBMA Vault'], 
      established: '1985', 
      capacity: '500+ tons',
      description: 'Primary European distribution center with LBMA-approved vaults',
    },
    { 
      location: 'New York, USA', 
      type: 'North American Hub', 
      capabilities: ['Storage', 'Clearing', 'Air Freight', 'COMEX Delivery'], 
      established: '1990', 
      capacity: '600+ tons',
      description: 'Strategic hub serving North American markets with COMEX connectivity',
    },
    { 
      location: 'Shanghai, China', 
      type: 'Asia-Pacific Hub', 
      capabilities: ['Storage', 'Customs', 'Sea Freight', 'SGE Access'], 
      established: '2008', 
      capacity: '450+ tons',
      description: 'Gateway to Asian markets with direct SGE membership',
    },
    { 
      location: 'Singapore', 
      type: 'Southeast Asian Hub', 
      capabilities: ['Storage', 'Sea Freight', 'Customs', 'Refining'], 
      established: '1995', 
      capacity: '300+ tons',
      description: 'Strategic hub for Southeast Asian precious metals trade',
    },
    { 
      location: 'Dubai, UAE', 
      type: 'Middle East Hub', 
      capabilities: ['Storage', 'Air Freight', 'Refining', 'DMCC Member'], 
      established: '2005', 
      capacity: '400+ tons',
      description: 'Premier Middle Eastern hub with DMCC accreditation',
    },
    { 
      location: 'Zurich, Switzerland', 
      type: 'European Vault Hub', 
      capabilities: ['Vault Storage', 'Private Transport', 'LBMA Vault'], 
      established: '1988', 
      capacity: '600+ tons',
      description: 'Premium vaulting services in Switzerland\'s secure infrastructure',
    },
  ];

  const services = [
    { icon: <Security />, title: 'Secure Transport', description: 'Armed escort and GPS-tracked vehicles' },
    { icon: <Verified />, title: 'Full Insurance', description: "Lloyd's of London coverage" },
    { icon: <Speed />, title: 'Express Delivery', description: '24-48 hour global delivery' },
    { icon: <Storage />, title: 'Vault Storage', description: 'LBMA-approved vaults worldwide' },
  ];

  const stats = [
    { value: '60+', label: 'Countries Served', icon: <Public />, description: 'Global reach' },
    { value: '24-48h', label: 'Global Delivery', icon: <Speed />, description: 'Express service' },
    { value: '100%', label: 'Insurance Coverage', icon: <Shield />, description: 'Full protection' },
    { value: '99.99%', label: 'On-Time Delivery', icon: <Verified />, description: 'Track record' },
  ];

  const transportMethods = [
    { icon: <Flight />, name: 'Air Freight', time: '24-48h', capacity: 'Up to 5 tons', routes: 'Global' },
    { icon: <DirectionsBoat />, name: 'Sea Freight', time: '5-15 days', capacity: 'Bulk shipments', routes: 'Major ports' },
    { icon: <LocalShipping />, name: 'Ground Transport', time: '1-3 days', capacity: 'Armored vehicles', routes: 'USA, Europe, China' },
  ];

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <AbstractBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section with Distribution Background Image */}
        <Box
          sx={{
            minHeight: '45vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: 'url("/images/distribution.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 6, md: 8 } }}>
            <Stack spacing={2.5}>
              <Chip
                label="GLOBAL DISTRIBUTION"
                icon={<LocalShipping sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.2),
                  color: '#D4AF37',
                  width: 'fit-content',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  backdropFilter: 'blur(5px)',
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                Global Distribution Network
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#D4AF37',
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                  fontWeight: 600,
                }}
              >
                Secure, Insured, and Efficient Logistics Worldwide
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#FFFFFF', 0.85),
                  maxWidth: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Secure, insured, and efficient logistics worldwide — delivering precious metals with precision and reliability across six continents
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Stats Dashboard */}
        <Container maxWidth="xl" sx={{ py: 6 }}>
          <Grid container spacing={2.5}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <StatCard 
                  icon={stat.icon} 
                  value={stat.value} 
                  label={stat.label} 
                  description={stat.description}
                  delay={index * 0.1}
                />
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Services Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 8 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Chip
                label="OUR SERVICES"
                icon={<Shield sx={{ fontSize: 12 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.1),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  fontSize: '0.65rem',
                  mb: 1.5,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 24, md: 32 },
                  fontWeight: 800,
                  color: '#0a0a2a',
                  mb: 1,
                }}
              >
                Comprehensive Logistics Solutions
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: alpha('#0a0a2a', 0.6), maxWidth: 600, mx: 'auto' }}
              >
                End-to-end distribution services for precious metals
              </Typography>
              <Box
                sx={{
                  width: 50,
                  height: 2,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 2,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={2.5}>
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <ServiceCard 
                    icon={service.icon} 
                    title={service.title} 
                    description={service.description}
                    delay={index * 0.1}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Strategic Hubs Section */}
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Chip
                label="STRATEGIC HUBS"
                icon={<Public sx={{ fontSize: 12 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  fontSize: '0.65rem',
                  mb: 1.5,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 24, md: 32 },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Global Distribution Centers
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 600, mx: 'auto' }}
              >
                Strategically located hubs serving international markets
              </Typography>
              <Box
                sx={{
                  width: 50,
                  height: 2,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 2,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={2.5}>
              {hubs.map((hub, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <HubCard 
                    location={hub.location}
                    type={hub.type}
                    description={hub.description}
                    established={hub.established}
                    capacity={hub.capacity}
                    capabilities={hub.capabilities}
                    delay={index * 0.1}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Transport Methods Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 6, md: 8 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Chip
                label="TRANSPORT METHODS"
                icon={<LocalShipping sx={{ fontSize: 12 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.1),
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  fontSize: '0.65rem',
                  mb: 1.5,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 24, md: 32 },
                  fontWeight: 800,
                  color: '#0a0a2a',
                  mb: 1,
                }}
              >
                Multi-Modal Logistics
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: alpha('#0a0a2a', 0.6), maxWidth: 600, mx: 'auto' }}
              >
                Flexible transport solutions for every requirement
              </Typography>
              <Box
                sx={{
                  width: 50,
                  height: 2,
                  background: 'linear-gradient(90deg, #D4AF37, #FFD700)',
                  margin: '0 auto',
                  mt: 2,
                  borderRadius: 2,
                }}
              />
            </Box>

            <Grid container spacing={2.5}>
              {transportMethods.map((method, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <TransportCard 
                    icon={method.icon}
                    name={method.name}
                    time={method.time}
                    capacity={method.capacity}
                    routes={method.routes}
                    delay={index * 0.15}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Logistics Network Section */}
        <Box sx={{ py: { xs: 6, md: 8 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.5s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="GLOBAL NETWORK"
                    icon={<Public sx={{ fontSize: 12 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.15),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      fontSize: '0.65rem',
                      mb: 1.5,
                    }}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: 24, md: 28 },
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1.5,
                    }}
                  >
                    Worldwide Logistics Network
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.8), mb: 2.5, lineHeight: 1.6 }}>
                    Our integrated logistics network spans five continents, utilizing air, sea, and ground 
                    transport to ensure your precious metals arrive safely and on time.
                  </Typography>
                  <List dense>
                    {[
                      { icon: <Flight />, title: 'Air Freight', description: 'Dedicated cargo routes to all major financial centers' },
                      { icon: <DirectionsBoat />, title: 'Sea Freight', description: 'Secure container shipping with real-time tracking' },
                      { icon: <GpsFixed />, title: 'GPS Tracking', description: '24/7 real-time location monitoring' },
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Box sx={{ color: '#D4AF37', fontSize: 20 }}>{item.icon}</Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.85rem' }}>{item.title}</Typography>}
                          secondary={<Typography sx={{ color: alpha('#FFFFFF', 0.5), fontSize: '0.7rem' }}>{item.description}</Typography>}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.5s ease-out 0.1s forwards`, opacity: 0 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      bgcolor: '#FFFFFF',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    <Diamond sx={{ fontSize: 44, color: '#D4AF37', mb: 1.5, animation: `${pulse} 2s ease-in-out infinite` }} />
                    <Typography variant="h6" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 1.5 }}>
                      Full Supply Chain Transparency
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mb: 2 }}>
                      Every shipment includes detailed documentation, chain of custody certificates, and real-time tracking access.
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                      <Chip label="Chain of Custody" size="small" sx={{ bgcolor: alpha('#D4AF37', 0.1), color: '#D4AF37', fontSize: '0.6rem', height: 24 }} />
                      <Chip label="Real-time Tracking" size="small" sx={{ bgcolor: alpha('#D4AF37', 0.1), color: '#D4AF37', fontSize: '0.6rem', height: 24 }} />
                      <Chip label="Digital Sign-off" size="small" sx={{ bgcolor: alpha('#D4AF37', 0.1), color: '#D4AF37', fontSize: '0.6rem', height: 24 }} />
                      <Chip label="Blockchain Verified" size="small" sx={{ bgcolor: alpha('#D4AF37', 0.1), color: '#D4AF37', fontSize: '0.6rem', height: 24 }} />
                    </Stack>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Security & Compliance Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 5, md: 6 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Grid container spacing={2.5} justifyContent="center">
              {[
                { icon: <Shield />, title: 'Armed Escorts', description: 'Security personnel for high-value shipments' },
                { icon: <GpsFixed />, title: 'Real-time Tracking', description: '24/7 GPS monitoring of all shipments' },
                { icon: <Verified />, title: 'Customs Clearance', description: 'Expert handling of international regulations' },
                { icon: <WorkspacePremium />, title: 'Insurance Coverage', description: "Lloyd's of London comprehensive policies" },
              ].map((item, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <Box sx={{ textAlign: 'center', p: 1.5, transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-4px)' } }}>
                    <Box sx={{ color: '#D4AF37', fontSize: 28, mb: 0.5 }}>{item.icon}</Box>
                    <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700, mb: 0.25 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), fontSize: '0.65rem' }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 6 }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Card
              elevation={0}
              sx={{
                p: 4,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                borderRadius: 3,
              }}
            >
              <WorkspacePremium sx={{ fontSize: 48, color: '#D4AF37', mb: 1.5 }} />
              <Typography variant="h5" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 1.5 }}>
                Ready to Ship?
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mb: 2.5 }}>
                Contact our logistics team for a customized distribution solution
              </Typography>
              <Button
                variant="contained"
                size="medium"
                sx={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  color: '#0a0a2a',
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  fontSize: '0.8rem',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 20px ${alpha('#D4AF37', 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Request Logistics Quote
              </Button>
            </Card>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Distribution;